import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';
import { buildSummary, readableContractColor } from '../h2h-summary-model';
import h2hWidgetMixin from '../h2h-widget-mixin';

let nextChartId = 0;

export default {
  components: { marketChangePrice },
  mixins: [h2hWidgetMixin],
  data() {
    return {
      chartClipId: `h2h-chart-clip-${++nextChartId}`,
      animationProgress: 1,
      animationFrame: null,
      hoverTimer: null,
      animationFinishedAt: null,
      animationGeneration: 0,
      chartObserver: null,
      motionPreference: null,
      chartReady: false,
      clock: Date.now(),
      clockTimer: null,
    };
  },
  computed: {
    summary() {
      return this.tile
        ? buildSummary(this.tile, this.chartReady ? this.chartRecords : [], this.clock)
        : null;
    },
    hasHistory() {
      return this.summary?.series.some((series) => series.hasHistory);
    },
    markers() {
      return this.summary.series.map((series) => {
        if (series.current === null) return null;
        if (!series.hasHistory || this.animationProgress === 1)
          return { x: 300, price: series.current };
        const time =
          this.summary.start + (this.summary.now - this.summary.start) * this.animationProgress;
        const before = series.points.filter((point) => point.time <= time).at(-1);
        const after = series.points.find((point) => point.time > time);
        if (!before || before.price === null) return null;
        if (!after) return { x: 300 * this.animationProgress, price: before.price };
        if (after.price === null) return null;
        const fraction = (time - before.time) / (after.time - before.time);
        return {
          x: 300 * this.animationProgress,
          price: before.price + (after.price - before.price) * fraction,
        };
      });
    },
    endLabelY() {
      const values = this.markers.map((marker) => (marker ? this.y(marker.price) : null));
      if (values.every((value) => value !== null) && Math.abs(values[0] - values[1]) < 22) {
        const center = Math.max(22, Math.min(84, (values[0] + values[1]) / 2));
        return values[0] <= values[1] ? [center - 11, center + 11] : [center + 11, center - 11];
      }
      return values;
    },
  },
  watch: {
    chartRecords() {
      this.refreshChart();
    },
    'tile.left.price'() {
      this.clock = Date.now();
    },
    'tile.right.price'() {
      this.clock = Date.now();
    },
  },
  methods: {
    async refreshChart() {
      this.stopAnimation();
      const generation = this.animationGeneration;
      this.chartReady = true;
      await this.$nextTick();
      if (generation === this.animationGeneration && this.hasHistory) this.prepareAnimation();
    },
    cancelHoverAnimation() {
      if (this.hoverTimer !== null) clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    },
    queueHoverAnimation(event) {
      this.cancelHoverAnimation();
      if (
        event.pointerType !== 'mouse' ||
        !window.matchMedia('(hover: hover)').matches ||
        !this.canReplayAnimation()
      )
        return;
      this.hoverTimer = setTimeout(() => {
        this.hoverTimer = null;
        if (!this.canReplayAnimation()) return;
        this.animationProgress = 0;
        this.playAnimation();
      }, 250);
    },
    canReplayAnimation() {
      return (
        this.hasHistory &&
        !this.motionPreference?.matches &&
        this.animationProgress === 1 &&
        (this.animationFinishedAt === null || performance.now() - this.animationFinishedAt >= 5000)
      );
    },
    stopAnimation() {
      this.cancelHoverAnimation();
      this.animationGeneration += 1;
      cancelAnimationFrame(this.animationFrame);
      this.chartObserver?.disconnect();
      this.animationProgress = 1;
    },
    prepareAnimation() {
      if (this.motionPreference?.matches) return;
      this.animationProgress = 0;
      if (typeof IntersectionObserver === 'undefined') return this.playAnimation();
      const generation = this.animationGeneration;
      const observer = new IntersectionObserver(
        (entries) => {
          if (generation !== this.animationGeneration) return;
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.playAnimation();
          }
        },
        { threshold: 0.4 },
      );
      this.chartObserver = observer;
      observer.observe(this.$refs.chart);
    },
    playAnimation() {
      if (this.motionPreference?.matches) return this.stopAnimation();
      const generation = this.animationGeneration;
      const start = performance.now();
      const frame = (now) => {
        if (generation !== this.animationGeneration) return;
        const fraction = Math.min(1, (now - start) / 3000);
        this.animationProgress = 1 - (1 - fraction) ** 3;
        if (fraction < 1) this.animationFrame = requestAnimationFrame(frame);
        else {
          this.animationFrame = null;
          this.animationFinishedAt = now;
        }
      };
      this.animationFrame = requestAnimationFrame(frame);
    },
    onMotionPreferenceChange() {
      if (this.motionPreference.matches) this.stopAnimation();
    },
    contractStyle(side) {
      // Compute both palettes; only body[data-theme] in CSS selects the active one.
      return {
        '--contract-color-light': side.color,
        '--contract-color-dark': readableContractColor(side.color, true),
      };
    },
    y(price) {
      return 10 + (100 - price) * 0.85;
    },
    chartPath(series) {
      let drawing = false;
      return series.points
        .map((point) => {
          if (point.price === null) {
            drawing = false;
            return '';
          }
          const x =
            (300 * (point.time - this.summary.start)) / (this.summary.now - this.summary.start);
          const command = drawing ? 'L' : 'M';
          drawing = true;
          return `${command}${x.toFixed(2)},${this.y(point.price).toFixed(2)}`;
        })
        .join(' ');
    },
  },
  mounted() {
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionPreference.addEventListener('change', this.onMotionPreferenceChange);
    this.refreshChart();
    this.clockTimer = setInterval(() => {
      this.clock = Date.now();
    }, 60000);
  },
  beforeUnmount() {
    this.stopAnimation();
    this.motionPreference?.removeEventListener('change', this.onMotionPreferenceChange);
    clearInterval(this.clockTimer);
  },
};
